import { Button } from 'components/button';
import {
    ContractContextProvider,
    useContract,
    useContractContext,
} from 'components/contract/contractContext';
import { ContractSidePanel } from 'components/contract/contractSidePanel';
import {
    CollectionProvider,
    CollectionState,
    useCollectionContext,
    useOptionalCollectionContext,
} from 'components/settings/collectionContext';
import { DocumentProvider, useOptionalDocumentContext } from 'components/settings/documentContext';
import { InputFromList } from 'components/settings/recordList';
import { useAccount } from 'components/settings/userProfileContext';
import { doc, FirestoreError, updateDoc, where } from 'firebase/firestore';
import { db } from 'firebaseStore';
import {
    Language,
    contractConverter,
    CONTRACT_COLLECTION_NAME,
    translationCoverter,
    TRANSLATIONS_COLLECTION_NAME,
    ContextProviderProps,
    Translation,
    LANGUAGE_COLLECTION_NAME,
    languageConverter,
    SECTION_COLLECTION_NAME,
    sectionConverter,
    COVERAGE_COLLECTION_NAME,
    coverageConverter,
    CLAUSE_COLLECTION_NAME,
    clauseConverter,
    SUBSECTION_COLLECTION_NAME,
    subsectionConverter,
    Section,
    Subsection,
    Coverage,
    Clause,
} from 'models';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { usePrompt, wordContract } from 'utils';
import './contractLanguagePage.css';

export const usePageLangauge = () => {
    const { languageId } = useParams();
    const [language, pending, error] = useOptionalDocumentContext(
        LANGUAGE_COLLECTION_NAME,
        languageId as string
    );
    const tag = `languageIndexId-${language?.indexId()}`;
    const translationsProviderProps: ContextProviderProps<Translation> = {
        converter: translationCoverter,
        constraints: [
            where('languageIndexId', '==', language?.indexId()),
            where('tags', 'array-contains', tag),
        ],
        collectionName: TRANSLATIONS_COLLECTION_NAME,
    };
    return [language, pending, error, translationsProviderProps] as [
        Language,
        boolean,
        FirestoreError | undefined,
        ContextProviderProps<Translation>
    ];
};

export const useTranslationsMap = () => {
    const [, , , translationsProps] = usePageLangauge();
    const [translations] = useOptionalCollectionContext(
        translationsProps.collectionName,
        translationsProps.constraints
    ) as CollectionState<Translation>;
    return new Map(
        (translations || []).map((record) => [`${record.targetId}:${record.targetField}`, record])
    );
};
export const useTranslationByTarget = (targetId: string, targetField: 'name' | 'content') => {
    const translationsMap = useTranslationsMap();
    return translationsMap.get(`${targetId}:${targetField}`);
};

const ExportComponent = () => {
    const [contract] = useContract();
    const [, , , sectionMap] = useCollectionContext(
        SECTION_COLLECTION_NAME
    ) as CollectionState<Section>;
    const [, , , subsectionMap] = useCollectionContext(
        SUBSECTION_COLLECTION_NAME
    ) as CollectionState<Subsection>;
    const [, , , coverageMap] = useCollectionContext(COVERAGE_COLLECTION_NAME, [
        where('productTypeIndexIds', 'array-contains', contract?.productTypeIndexId),
    ]) as CollectionState<Coverage>;
    const [, , , clauseMap] = useCollectionContext(CLAUSE_COLLECTION_NAME, [
        where('productTypeIndexIds', 'array-contains', contract?.productTypeIndexId),
    ]) as CollectionState<Clause>;
    const translationsMap = useTranslationsMap();
    return (
        <Button
            style={{ width: '97px' }}
            onClick={() =>
                wordContract(
                    contract,
                    sectionMap,
                    subsectionMap,
                    coverageMap,
                    clauseMap,
                    translationsMap
                )
            }
        >
            Export
        </Button>
    );
};

export const ContractLanguagePage = () => {
    const { contractId, languageId } = useParams();
    if (!contractId) {
        return <div>Cannot load contract with id '${contractId}'</div>;
    }
    if (!languageId) {
        return <div>Cannot load languageId with id '${languageId}'</div>;
    }
    return (
        <DocumentProvider
            collectionName={CONTRACT_COLLECTION_NAME}
            converter={contractConverter}
            id={contractId}
        >
            <DocumentProvider
                collectionName={LANGUAGE_COLLECTION_NAME}
                converter={languageConverter}
                id={languageId}
            >
                <ContractLanguagePageWrapper />
            </DocumentProvider>
        </DocumentProvider>
    );
};

const LanguageDropdown = () => {
    const account = useAccount();
    const { contractId } = useParams();
    const { contract } = useContractContext();

    const [language] = usePageLangauge();
    const navigate = useNavigate();
    const [, , , languageMap] = useCollectionContext(LANGUAGE_COLLECTION_NAME);
    const languages = account
        .getMarkets()
        .concat(contract.markets)
        .map((market) => languageMap.get(market.languageIndexId))
        .filter((language_) => !!language_) as Language[];
    const dropDownValues = languages.map((language_) => {
        return { name: language_.name, id: language_.id };
    });
    return (
        <InputFromList
            id="contractLanguage"
            value={{ name: language.name, id: language.indexId() }}
            values={dropDownValues}
            onSelect={(inputValue) =>
                inputValue &&
                navigate(`/contract-languages/${contractId}/language/${inputValue.id}`)
            }
        />
    );
};

const ContractLanguagePageWrapper = () => {
    const [contract, contractPending] = useContract();
    const [language, languagePenging, , translationsProps] = usePageLangauge();
    if (contractPending) {
        return <div>Loading contract</div>;
    }
    if (languagePenging) {
        return <div>Loading language</div>;
    }
    if (!contract) {
        return <div>Could not load contract</div>;
    }
    if (!language) {
        return <div>Could not load language</div>;
    }
    return (
        <CollectionProvider {...translationsProps}>
            <CollectionProvider
                collectionName={SECTION_COLLECTION_NAME}
                converter={sectionConverter}
            >
                <CollectionProvider
                    collectionName={SUBSECTION_COLLECTION_NAME}
                    converter={subsectionConverter}
                >
                    <CollectionProvider
                        collectionName={COVERAGE_COLLECTION_NAME}
                        converter={coverageConverter}
                        constraints={[
                            where(
                                'productTypeIndexIds',
                                'array-contains',
                                contract.productTypeIndexId
                            ),
                        ]}
                    >
                        <CollectionProvider
                            collectionName={CLAUSE_COLLECTION_NAME}
                            converter={clauseConverter}
                            constraints={[
                                where(
                                    'productTypeIndexIds',
                                    'array-contains',
                                    contract.productTypeIndexId
                                ),
                            ]}
                        >
                            <CollectionProvider
                                collectionName={LANGUAGE_COLLECTION_NAME}
                                converter={languageConverter}
                            >
                                <ContractContextProvider>
                                    <ContractLanguagePageContent />
                                </ContractContextProvider>
                            </CollectionProvider>
                        </CollectionProvider>
                    </CollectionProvider>
                </CollectionProvider>
            </CollectionProvider>
        </CollectionProvider>
    );
};

const ContractLanguagePageContent = () => {
    const { contract, originalContract, expCol, setExpCol } = useContractContext();
    const [language] = usePageLangauge();
    const hasChanged = !contract.equals(originalContract);
    usePrompt('Unsaved changes - are you sure?', hasChanged);
    const navigate = useNavigate();
    const onSave = (next?: boolean) => {
        const newUrl = `/contract-languages/${contract.id}/translation/${language?.id}`;
        if (!hasChanged) {
            return next && navigate(newUrl);
        }
        const docRef = doc(db, CONTRACT_COLLECTION_NAME, contract.id);
        return updateDoc(docRef, contract.toFirestore()).then(
            () => {
                next && navigate(newUrl);
            },
            (error) => {
                console.error('ContractDisplay updateDoc error', error);
            }
        );
    };

    return (
        <div
            style={{
                padding: '0 70px 70px 70px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    marginBottom: '20px',
                }}
            >
                <div
                    style={{
                        flexBasis: '373px',
                        flexGrow: '1',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <div
                        style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <div style={{ fontSize: '24px' }}>{contract.name}</div>
                    </div>
                </div>

                <div
                    style={{
                        flexBasis: '373px',
                        flexGrow: '1',
                        fontSize: '34px',
                        justifyContent: 'center',
                        display: 'flex',
                    }}
                >
                    Language: {language.name}
                </div>
                <div
                    style={{
                        flexBasis: '373px',
                        flexGrow: '1',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                    }}
                >
                    <div
                        style={{
                            cursor: 'pointer',
                            display: 'flex',
                            minWidth: '65px',
                            marginRight: '16px',
                        }}
                        onClick={() => {
                            const nextAllExpCol =
                                expCol.getValue('all') !== 'expanded' ? 'expanded' : 'collapsed';
                            setExpCol('all', nextAllExpCol);
                        }}
                    >
                        {expCol.getValue('all') !== 'expanded' ? 'Expand all' : 'Collapse All'}
                    </div>
                    <ExportComponent />
                    <NavLink
                        to={`/contract-languages/${encodeURIComponent(contract.id)}`}
                        style={{ color: 'inherit', textDecoration: 'inherit' }}
                    >
                        <Button style={{ marginLeft: '16px', width: '97px' }}>Back</Button>
                    </NavLink>
                    <Button
                        style={{ marginLeft: '16px', width: '97px' }}
                        onClick={() => onSave(true)}
                    >
                        Next
                    </Button>
                </div>
            </div>
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                }}
            >
                <div style={{ fontSize: '24px', width: '241px' }}>
                    <LanguageDropdown />
                </div>
            </div>
            <ContractDisplay />
        </div>
    );
};

const ContractDisplay = () => {
    const account = useAccount();
    const [language] = usePageLangauge();
    const translated = !account
        .getMarkets()
        .find((market) => market.languageIndexId === language.indexId());
    return (
        <div style={{ width: '100%' }}>
            <ContractSidePanel translated={translated} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}></div>
        </div>
    );
};
